import { useState } from "react";
import type { DraftValidationIssue } from "@sga/shared";

export interface PublishDiff {
  added: { key: string; label?: string }[];
  removed: { key: string; label?: string }[];
  renamed: { key: string; label?: string; detail?: string }[];
  requirementChanged: { key: string; label?: string; detail?: string }[];
}

/**
 * The publish flow: diff, validation results, and a typed confirmation when a field with existing
 * data is being removed (docs/04-BUILD-PLAN.md Phase 4).
 *
 * The typed confirmation is not ceremony. Removing a published field is the one action here that
 * can quietly orphan data people already submitted, so it should cost a deliberate keystroke.
 */
export const PublishModal = ({
  diff,
  issues,
  isPublishing,
  onPublish,
  onClose,
  onFixIssue,
}: {
  diff: PublishDiff | null;
  issues: DraftValidationIssue[];
  isPublishing: boolean;
  onPublish: () => void;
  onClose: () => void;
  /** Jumps to wherever the issue is actually fixed. Absent when nothing here can be jumped to. */
  onFixIssue?: (issue: DraftValidationIssue) => void;
}): JSX.Element => {
  const [typed, setTyped] = useState("");

  const removals = diff?.removed ?? [];
  const needsTypedConfirmation = removals.length > 0;
  const confirmationPhrase = "REMOVE";
  const blocked = issues.length > 0;
  const canPublish =
    !blocked && !isPublishing && (!needsTypedConfirmation || typed === confirmationPhrase);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="publish-title"
    >
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-card p-5 shadow-lg">
        <h2 id="publish-title" className="font-heading text-lg font-semibold">
          Publish this form
        </h2>

        {blocked && (
          <div className="mt-3 rounded-md border border-brand-red bg-brand-red/5 p-3">
            <p className="text-sm font-medium text-brand-red">
              This form cannot be published yet — {issues.length} problem
              {issues.length === 1 ? "" : "s"} to fix:
            </p>
            <ul className="mt-2 space-y-1.5">
              {issues.map((issue, index) => (
                <li key={index} className="text-xs text-brand-red">
                  • {issue.message}
                  {onFixIssue && (
                    <button
                      type="button"
                      onClick={() => onFixIssue(issue)}
                      className="ml-1 whitespace-nowrap font-medium underline underline-offset-2"
                    >
                      Take me there
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {diff && (
          <div className="mt-4 space-y-3 text-sm">
            {(
              [
                ["Added", diff.added, "text-primary"],
                ["Removed", diff.removed, "text-brand-red"],
                ["Renamed", diff.renamed, "text-foreground"],
                ["Requirement changed", diff.requirementChanged, "text-foreground"],
              ] as const
            ).map(([heading, entries, tone]) =>
              entries.length > 0 ? (
                <div key={heading}>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {heading}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {entries.map((entry) => (
                      <li key={entry.key} className={`text-xs ${tone}`}>
                        <code>{entry.key}</code>
                        {"detail" in entry && entry.detail ? ` — ${entry.detail}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null,
            )}

            {diff.added.length === 0 &&
              diff.removed.length === 0 &&
              diff.renamed.length === 0 &&
              diff.requirementChanged.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No schema changes since the live version.
                </p>
              )}
          </div>
        )}

        {needsTypedConfirmation && (
          <div className="mt-4 rounded-md border border-brand-red p-3">
            <p className="text-xs">
              {removals.length} field{removals.length === 1 ? "" : "s"} will be removed. Answers
              already submitted are never deleted, but the{" "}
              {removals.length === 1 ? "field" : "fields"} will no longer appear on the form. Type{" "}
              <strong>{confirmationPhrase}</strong> to confirm.
            </p>
            <label htmlFor="publish-confirm" className="sr-only">
              Type {confirmationPhrase} to confirm removal
            </label>
            <input
              id="publish-confirm"
              className="mt-2 w-full rounded border px-2 py-1 text-sm"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
            />
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-3 py-1.5 text-sm hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onPublish}
            disabled={!canPublish}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            {isPublishing ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
};
